import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Flex,
  Heading,
  Button,
  Grid,
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  Text,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Checkbox,
  VStack,
  HStack,
  useDisclosure,
  useToast,
  List,
  ListItem,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  IconButton,
  useBreakpointValue,
  useMediaQuery,
  useColorModeValue,
  Spinner,
  Center,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
} from '@chakra-ui/react';
import { useApp } from '../context/AppContext';
import HijriDatePicker from './HijriDatePicker';
import { arabicToEnglish } from '../utils/numberConverter';
import AuditLogModal from './AuditLogModal';
import { caseCategoriesService, caseTypesService } from '../services/apiService';

function SearchableSelect({
  label,
  items,
  value,
  onChange,
  isLoading,
  isDisabled,
  placeholder,
  getLabel,
  getValue,
  emptyText = 'لا توجد نتائج',
}) {
  const containerRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const dropdownBg = useColorModeValue('white', 'gray.800');
  const dropdownBorder = useColorModeValue('gray.200', 'gray.600');
  const dropdownShadow = useColorModeValue('md', 'dark-lg');
  const itemHoverBg = useColorModeValue('gray.50', 'gray.700');
  const itemSelectedBg = useColorModeValue('primary.50', 'primary.900');
  const emptyTextColor = useColorModeValue('gray.500', 'gray.400');

  const selectedItem = items.find((it) => String(getValue(it)) === String(value));
  const selectedLabel = selectedItem ? getLabel(selectedItem) : '';

  const filtered = items.filter((it) =>
    (getLabel(it) || '').toLowerCase().includes((query || '').toLowerCase())
  );

  useEffect(() => {
    // Keep display text in sync when selection changes programmatically
    if (!isOpen) setQuery(selectedLabel);
  }, [value, selectedLabel, isOpen]);

  // Close when clicking outside
  useEffect(() => {
    if (!isOpen) return;
    const onDocMouseDown = (e) => {
      const el = containerRef.current;
      if (!el) return;
      if (!el.contains(e.target)) {
        setIsOpen(false);
        setQuery(selectedLabel);
      }
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [isOpen, selectedLabel]);

  return (
    <FormControl isDisabled={isDisabled}>
      <FormLabel>{label}</FormLabel>
      <Box ref={containerRef} position="relative" w="100%">
        <Input
          placeholder={placeholder}
          value={isOpen ? query : (selectedLabel || '')}
          onChange={(e) => {
            setQuery(e.target.value);
            if (!isDisabled) setIsOpen(true);
          }}
          onFocus={() => {
            if (isDisabled) return;
            setIsOpen(true);
            // if user focuses and nothing typed, allow showing full list by clearing query
            if (!query) setQuery('');
          }}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              setIsOpen(false);
              setQuery(selectedLabel);
            }
          }}
          isReadOnly={isDisabled}
          pr={isLoading ? '2.25rem' : undefined}
        />
        {isLoading ? (
          <Box position="absolute" right="0.6rem" top="50%" transform="translateY(-50%)">
            <Spinner size="sm" />
          </Box>
        ) : null}

        {isOpen && !isDisabled ? (
          <Box
            position="absolute"
            top="calc(100% + 6px)"
            left={0}
            right={0}
            bg={dropdownBg}
            border="1px solid"
            borderColor={dropdownBorder}
            borderRadius="md"
            boxShadow={dropdownShadow}
            zIndex={2000}
            p={2}
            maxH="240px"
            overflowY="auto"
          >
            {filtered.length === 0 ? (
              <Text fontSize="sm" color={emptyTextColor} p={2}>
                {emptyText}
              </Text>
            ) : (
              <List spacing={1}>
                {filtered.map((it) => {
                  const id = getValue(it);
                  const text = getLabel(it);
                  const isSelected = String(id) === String(value);
                  return (
                    <ListItem
                      key={String(id)}
                      px={3}
                      py={2}
                      borderRadius="md"
                      bg={isSelected ? itemSelectedBg : 'transparent'}
                      _hover={{ bg: itemHoverBg }}
                      cursor="pointer"
                      onMouseDown={(e) => {
                        // prevent input blur before selection
                        e.preventDefault();
                        onChange(String(id));
                        setIsOpen(false);
                        setQuery(text);
                      }}
                    >
                      <Text fontSize="sm">{text}</Text>
                    </ListItem>
                  );
                })}
              </List>
            )}
          </Box>
        ) : null}
      </Box>
    </FormControl>
  );
}

const CaseManagement = () => {
  const {
    cases,
    users,
    currentUser,
    addCase,
    updateCase,
    deleteCase,
    canCreateCase,
    canEditCase,
    canDeleteCase,
    loadAllData,
    loadUsers,
    casesPagination,
    loading,
    loadingCases,
    loadingUsers
  } = useApp();
  const navigate = useNavigate();
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isDeleteOpen,
    onOpen: onDeleteOpen,
    onClose: onDeleteClose,
  } = useDisclosure();
  const {
    isOpen: isAuditLogOpen,
    onOpen: onAuditLogOpen,
    onClose: onAuditLogClose,
  } = useDisclosure();
  const {
    isOpen: isViewCaseOpen,
    onOpen: onViewCaseOpen,
    onClose: onViewCaseClose,
  } = useDisclosure();
  const [selectedCaseId, setSelectedCaseId] = useState(null);
  const [viewingCase, setViewingCase] = useState(null);
  const [editingCase, setEditingCase] = useState(null);
  const [deleteCaseId, setDeleteCaseId] = useState(null);
  const [formData, setFormData] = useState({
    referenceLetterNumber: '',
    requestingAuthority: '',
    letterDate: '',
    caseSubject: '',
    assignedUsers: [],
    categoryId: '',
    typeId: '',
  });
  const [categories, setCategories] = useState([]);
  const [types, setTypes] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [loadingTypes, setLoadingTypes] = useState(false);
  // SearchableSelect manages its own query (no separate state needed)
  // Always start with page 1, we'll restore from localStorage after data loads
  const [currentPage, setCurrentPage] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [filters, setFilters] = useState({
    caseNumber: '',
    referenceLetterNumber: '',
    dateFrom: '', // Gregorian date for API
    dateTo: ''    // Gregorian date for API
  });
  const [tempFilters, setTempFilters] = useState({
    caseNumber: '',
    referenceLetterNumber: '',
    dateFrom: '', // Gregorian date for API
    dateTo: ''    // Gregorian date for API
  });
  const [showFilters, setShowFilters] = useState(false);
  
  // Cache refs to track what was last loaded (for filter comparison only)
  // Note: We rely on casesPagination.page to check if page matches, not refs
  const lastLoadedPageRef = useRef(null);
  const lastLoadedFiltersRef = useRef(null);
  
  // Restore last page from localStorage after data loads, but validate it exists
  useEffect(() => {
    // Only restore if we have pagination data and cases are loaded
    if (casesPagination.totalPages > 0 && !loadingCases) {
      const savedPage = localStorage.getItem('cases_last_page');
      if (savedPage) {
        const page = parseInt(savedPage, 10);
        // Validate that the saved page exists (not greater than totalPages)
        if (page > 0 && page <= casesPagination.totalPages) {
          if (page !== currentPage) {
            setCurrentPage(page);
          }
        } else if (page > casesPagination.totalPages) {
          // If saved page is greater than total pages, go to last page
          const lastPage = casesPagination.totalPages;
          if (lastPage > 0 && lastPage !== currentPage) {
            setCurrentPage(lastPage);
          }
        }
        // Always clear the saved page after using it (whether valid or not)
        localStorage.removeItem('cases_last_page');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [casesPagination.totalPages, loadingCases]);

  // Initialize filters ref from current state when component mounts (if data already exists)
  useEffect(() => {
    if (cases.length > 0 && casesPagination.page && lastLoadedFiltersRef.current === null) {
      lastLoadedFiltersRef.current = { ...filters };
      lastLoadedPageRef.current = casesPagination.page;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  // Responsive breakpoints
  const showPageNumbers = useBreakpointValue({ base: false, sm: true });
  const showPageInfo = useBreakpointValue({ base: false, md: true });
  const buttonSize = useBreakpointValue({ base: 'sm', md: 'md' });
  const pageButtonSize = useBreakpointValue({ base: 'xs', md: 'sm' });
  const [isLargeScreen] = useMediaQuery('(min-width: 768px)');
  const tableRowHoverBg = useColorModeValue('gray.50', 'gray.700');
  const caseNumberColor = useColorModeValue('primary.700', 'primary.400'); // Darker in light mode for better contrast
  const assignedUsersBg = useColorModeValue('gray.50', 'gray.700');
  const assignedUserCardBg = useColorModeValue('white', 'gray.800');
  const assignedUserCardHoverBg = useColorModeValue('primary.50', 'primary.900');
  const assignedUserTextColor = useColorModeValue('gray.600', 'gray.400');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  // View Case Modal colors
  const modalLabelColor = useColorModeValue('gray.700', 'gray.300');
  const modalValueColor = useColorModeValue('gray.800', 'gray.200');
  const modalUserCardBg = useColorModeValue('gray.50', 'gray.700');
  const modalUserCardBorder = useColorModeValue('gray.200', 'gray.600');
  const modalUserTextColor = useColorModeValue('gray.600', 'gray.400');

  // Since filtering is done in backend, we just display all cases returned
  // The backend already filters based on role (admin sees all, supervisor sees their region cases, examiner sees their cases)
  const displayCases = cases;

  // Validate currentPage against totalPages after data loads
  useEffect(() => {
    if (casesPagination.totalPages > 0 && currentPage > casesPagination.totalPages && !loadingCases) {
      // If current page exceeds total pages, go to last page
      const lastPage = casesPagination.totalPages;
      setCurrentPage(lastPage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [casesPagination.totalPages, loadingCases]);

  // Load cases when page or filters change
  useEffect(() => {
    if (currentPage && currentPage > 0) {
      // Check if pagination matches current page and we have cases
      const paginationMatches = casesPagination.page === currentPage;
      const hasCasesForPage = cases.length > 0 && paginationMatches;
      
      // Check if filters changed (compare with last loaded filters from ref)
      const filtersKey = JSON.stringify(filters);
      const lastFiltersKey = lastLoadedFiltersRef.current ? JSON.stringify(lastLoadedFiltersRef.current) : null;
      const filtersChanged = lastFiltersKey !== null && filtersKey !== lastFiltersKey;
      
      // Only load if we don't have cases for current page OR filters changed
      const shouldLoad = !hasCasesForPage || filtersChanged;
      
      if (shouldLoad) {
      loadAllData(currentPage, false, filters);
        lastLoadedPageRef.current = currentPage;
        lastLoadedFiltersRef.current = { ...filters };
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, filters]);

  // Ensure users list is available for assignment after refresh.
  // On initial login we load users, but on page refresh AppContext intentionally lazy-loads.
  useEffect(() => {
    if (!currentUser) return;
    if (loadingUsers) return;
    if (users && users.length > 0) return;

    const locationId = currentUser?.locationId || currentUser?.location_id || null;
    loadUsers(1, false, locationId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);
  
  // Sync tempFilters when filters change (when search is applied)
  useEffect(() => {
    setTempFilters(filters);
  }, [filters]);

  // Load categories ONCE (when modal opens the first time).
  // Search is client-side (no API calls while typing).
  useEffect(() => {
    if (!isOpen) return;
    if (loadingCategories) return;
    if (categories.length > 0) return;

    const loadCategories = async () => {
      setLoadingCategories(true);
      try {
        const data = await caseCategoriesService.getAll(); // fetch all once
        setCategories(data || []);
      } catch (error) {
        console.error('Error loading categories:', error);

        const status = error?.response?.status;
        const serverMsg = error?.response?.data?.message;
        const description =
          status === 401
            ? 'غير مصرح. سجّل دخولك مرة ثانية.'
            : status === 500
              ? 'خطأ من السيرفر. تأكد أنك شغّلت migrations/seed للأصناف.'
              : serverMsg || 'فشل في تحميل الأصناف';

        toast({
          title: 'خطأ',
          description,
          status: 'error',
          duration: 4000,
          isClosable: true,
        });
      } finally {
        setLoadingCategories(false);
      }
    };

    loadCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Load types ONLY after selecting a category (fetch once per category selection).
  useEffect(() => {
    if (!isOpen) return;

    const loadTypes = async () => {
      if (!formData.categoryId) {
        setTypes([]);
        setFormData((prev) => ({ ...prev, typeId: '' }));
        return;
      }

      setLoadingTypes(true);
      try {
        const data = await caseTypesService.getAll(formData.categoryId); // fetch all for category once
        setTypes(data || []);
      } catch (error) {
        console.error('Error loading types:', error);

        const status = error?.response?.status;
        const serverMsg = error?.response?.data?.message;
        const description =
          status === 401
            ? 'غير مصرح. سجّل دخولك مرة ثانية.'
            : status === 500
              ? 'خطأ من السيرفر. تأكد أنك شغّلت migrations/seed للأصناف.'
              : serverMsg || 'فشل في تحميل الأنواع';

        toast({
          title: 'خطأ',
          description,
          status: 'error',
          duration: 4000,
          isClosable: true,
        });
      } finally {
        setLoadingTypes(false);
      }
    };

    loadTypes();
  }, [isOpen, formData.categoryId, toast]);

  const handleOpenModal = (caseItem = null) => {
    if (caseItem) {
      setEditingCase(caseItem);
      setFormData({
        referenceLetterNumber: caseItem.referenceLetterNumber || '',
        requestingAuthority: caseItem.requestingAuthority || '',
        letterDate: caseItem.letterDate || '',
        caseSubject: caseItem.caseSubject || '',
        assignedUsers: caseItem.assignedUsers || [],
        categoryId: caseItem.categoryId || caseItem.category?.id || '',
        typeId: caseItem.typeId || caseItem.type?.id || '',
      });
    } else {
      setEditingCase(null);
      setFormData({
        referenceLetterNumber: '',
        requestingAuthority: '',
        letterDate: '',
        caseSubject: '',
        assignedUsers: [],
        categoryId: '',
        typeId: ''
      });
    }
    onOpen();
  };

  const handleCloseModal = () => {
    onClose();
    setEditingCase(null);
    setFormData({
      referenceLetterNumber: '',
      requestingAuthority: '',
      letterDate: '',
      caseSubject: '',
      assignedUsers: [],
      categoryId: '',
      typeId: ''
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    
    setIsSubmitting(true);
    try {
    if (editingCase) {
        await updateCase(editingCase.id, formData);
      toast({
        title: 'تم التحديث',
        description: 'تم تحديث القضية بنجاح',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } else {
        // Add locationId from currentUser if available
        const caseDataWithLocation = {
          ...formData,
          locationId: currentUser?.locationId || currentUser?.location_id || null
        };
        await addCase(caseDataWithLocation);
      toast({
        title: 'تمت الإضافة',
        description: 'تم إضافة القضية بنجاح',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    }
    handleCloseModal();
    } catch (error) {
      console.error('Error saving case:', error);
      // Extract error message from Laravel validation errors
      let errorMessage = 'فشل في حفظ القضية';
      if (error.response?.data) {
        if (error.response.data.message) {
          errorMessage = error.response.data.message;
        } else if (error.response.data.errors) {
          // Laravel validation errors format
          const errors = error.response.data.errors;
          const firstError = Object.values(errors)[0];
          errorMessage = Array.isArray(firstError) ? firstError[0] : firstError;
        } else if (error.response.data.error) {
          errorMessage = error.response.data.error;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      toast({
        title: 'خطأ',
        description: errorMessage,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteClick = (id) => {
    setDeleteCaseId(id);
    onDeleteOpen();
  };

  const handleDeleteConfirm = async () => {
    if (isDeleting) return;
    
    setIsDeleting(true);
    try {
      await deleteCase(deleteCaseId);
    toast({
      title: 'تم الحذف',
      description: 'تم حذف القضية بنجاح',
      status: 'success',
      duration: 3000,
      isClosable: true,
    });
    onDeleteClose();
    setDeleteCaseId(null);
    } catch (error) {
      console.error('Error deleting case:', error);
      toast({
        title: 'خطأ',
        description: error.response?.data?.message || error.message || 'فشل في حذف القضية',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const toggleUserAssignment = (userId) => {
    const assignedUsers = formData.assignedUsers || [];
    // Convert both to strings for comparison
    const userIdStr = String(userId);
    const isAssigned = assignedUsers.some(id => String(id) === userIdStr);
    
    if (isAssigned) {
      setFormData({
        ...formData,
        assignedUsers: assignedUsers.filter(id => String(id) !== userIdStr)
      });
    } else {
      setFormData({
        ...formData,
        assignedUsers: [...assignedUsers, userId]
      });
    }
  };

  const handleTempFilterChange = (field, value) => {
    // Convert Arabic numerals to English for numeric fields
    let convertedValue = value;
    if (field === 'caseNumber' || field === 'referenceLetterNumber') {
      convertedValue = arabicToEnglish(value);
    }
    setTempFilters(prev => ({
      ...prev,
      [field]: convertedValue
    }));
  };

  const handleSearch = async () => {
    // Validate dates: dateFrom must be <= dateTo
    if (tempFilters.dateFrom && tempFilters.dateTo) {
      const dateFrom = new Date(tempFilters.dateFrom);
      const dateTo = new Date(tempFilters.dateTo);
      
      if (dateFrom > dateTo) {
        toast({
          title: 'خطأ في التواريخ',
          description: 'تاريخ البداية يجب أن يكون قبل أو يساوي تاريخ النهاية',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        return;
      }
    }
    
    setFilters(tempFilters);
    setCurrentPage(1); // Reset to first page when searching
    // Load data immediately with new filters
    try {
      await loadAllData(1, false, tempFilters);
    } catch (error) {
      // Handle validation errors from backend
      if (error.response && error.response.status === 422) {
        const errorMessage = error.response.data?.message || 'خطأ في التواريخ';
        toast({
          title: 'خطأ في التحقق',
          description: errorMessage,
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    }
  };

  const handleClearFilters = () => {
    const emptyFilters = {
      caseNumber: '',
      referenceLetterNumber: '',
      dateFrom: '',
      dateTo: ''
    };
    setTempFilters(emptyFilters);
    setFilters(emptyFilters);
    setCurrentPage(1);
  };

  const hasActiveFilters = filters.caseNumber || filters.referenceLetterNumber || filters.dateFrom || filters.dateTo;

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={6} direction={{ base: 'column', md: 'row' }} gap={4}>
        <Heading size="lg">إدارة القضايا</Heading>
        <HStack spacing={2}>
          <Button 
            variant="outline" 
            onClick={() => setShowFilters(!showFilters)}
            size={{ base: 'sm', md: 'md' }}
          >
            {showFilters ? 'إخفاء البحث' : 'البحث والفلترة'}
          </Button>
          {canCreateCase() && (
            <Button colorScheme="primary" onClick={() => handleOpenModal()}>
              إضافة قضية جديدة
            </Button>
          )}
        </HStack>
      </Flex>

      {/* Filter Section */}
      {showFilters && (
        <Card mb={6} boxShadow="md">
          <CardBody>
            <VStack spacing={4} align="stretch">
              <Flex direction={{ base: 'column', md: 'row' }} gap={4}>
                <FormControl>
                  <FormLabel>رقم القضية</FormLabel>
                  <Input
                    placeholder="ابحث برقم القضية"
                    value={tempFilters.caseNumber}
                    onChange={(e) => handleTempFilterChange('caseNumber', e.target.value)}
                    dir="ltr"
                    textAlign="left"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        handleSearch();
                      }
                    }}
                  />
                </FormControl>
                
                <FormControl>
                  <FormLabel>رقم خطاب الإحالة</FormLabel>
                  <Input
                    placeholder="ابحث برقم خطاب الإحالة"
                    value={tempFilters.referenceLetterNumber}
                    onChange={(e) => handleTempFilterChange('referenceLetterNumber', e.target.value)}
                    dir="ltr"
                    textAlign="left"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        handleSearch();
                      }
                    }}
                  />
                </FormControl>
              </Flex>
              
              <Flex direction={{ base: 'column', md: 'row' }} gap={4}>
                <FormControl>
                  <FormLabel>تاريخ الإنشاء من</FormLabel>
                  <HijriDatePicker
                    value={tempFilters.dateFrom}
                    onChange={(date) => handleTempFilterChange('dateFrom', date)}
                    placeholder="اختر تاريخ البداية"
                  />
                </FormControl>
                
                <FormControl>
                  <FormLabel>تاريخ الإنشاء إلى</FormLabel>
                  <HijriDatePicker
                    value={tempFilters.dateTo}
                    onChange={(date) => handleTempFilterChange('dateTo', date)}
                    placeholder="اختر تاريخ النهاية"
                  />
                </FormControl>
              </Flex>
              
              <HStack spacing={2} justify="flex-start">
                <Button
                  colorScheme="primary"
                  onClick={handleSearch}
                  isLoading={loading}
                >
                  بحث
                </Button>
                {hasActiveFilters && (
                  <Button
                    variant="ghost"
                    colorScheme="red"
                    onClick={handleClearFilters}
                  >
                    مسح الفلاتر
                  </Button>
                )}
              </HStack>
            </VStack>
          </CardBody>
        </Card>
      )}

      {loadingCases ? (
        <Center py={10}>
          <VStack spacing={4}>
            <Spinner size="xl" color="primary.500" thickness="4px" speed="0.65s" />
            <Text color="gray.600">جاري تحميل القضايا...</Text>
          </VStack>
        </Center>
      ) : displayCases.length === 0 ? (
        <Card>
          <CardBody>
            <Text textAlign="center" color="gray.500">
              لا توجد قضايا متاحة
            </Text>
          </CardBody>
        </Card>
      ) : isLargeScreen ? (
        // Table view for large screens
        <Box overflowX="auto">
          <Table variant="simple" size="md">
            <Thead>
              <Tr>
                <Th>رقم القضية</Th>
                <Th>رقم خطاب الإحالة</Th>
                <Th>الجهة الطالبة</Th>
                <Th>تاريخ الخطاب</Th>
                <Th>موضوع القضية</Th>
                <Th textAlign="center">الإجراءات</Th>
              </Tr>
            </Thead>
            <Tbody>
              {displayCases.map((caseItem) => (
                <Tr key={caseItem.id} _hover={{ bg: tableRowHoverBg }}>
                  <Td fontWeight="semibold" color={caseNumberColor} dir="ltr" textAlign="left">
                    {caseItem.caseNumber}
                  </Td>
                  <Td>{caseItem.referenceLetterNumber || 'غير محدد'}</Td>
                  <Td>{caseItem.requestingAuthority || 'غير محدد'}</Td>
                  <Td>{caseItem.letterDateHijriFormatted || 'غير محدد'}</Td>
                  <Td maxW="300px" isTruncated title={caseItem.caseSubject || 'غير محدد'}>
                    {caseItem.caseSubject || 'غير محدد'}
                  </Td>
                  <Td>
                    <Menu>
                      <MenuButton
                        as={Button}
                        size="sm"
                        variant="outline"
                        rightIcon={
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M7 10l5 5 5-5z" fill="currentColor"/>
                          </svg>
                        }
                      >
                        الإجراءات
                      </MenuButton>
                      <MenuList>
                        <MenuItem
                          icon={
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" fill="currentColor"/>
                            </svg>
                          }
                          _hover={{}}
                          onClick={() => {
                            setViewingCase(caseItem);
                            onViewCaseOpen();
                          }}
                        >
                          عرض القضية
                        </MenuItem>
                        <MenuItem
                          icon={
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z" fill="currentColor"/>
                            </svg>
                          }
                          _hover={{}}
                          onClick={() => {
                            setSelectedCaseId(caseItem.id);
                            onAuditLogOpen();
                          }}
                        >
                          سجل العمليات
                        </MenuItem>
                        <MenuItem
                          icon={
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" fill="currentColor"/>
                            </svg>
                          }
                          _hover={{}}
                          onClick={() => {
                            // Save current page before navigating
                            localStorage.setItem('cases_last_page', String(currentPage));
                            navigate(`/samples/${caseItem.id}`);
                          }}
                        >
                          العينات
                        </MenuItem>
                        <MenuItem
                          icon={
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z" fill="currentColor"/>
                            </svg>
                          }
                          _hover={{}}
                          onClick={() => {
                            // Save current page before navigating
                            localStorage.setItem('cases_last_page', String(currentPage));
                            navigate(`/reports/technical/${caseItem.id}`);
                          }}
                        >
                          التقرير الفني
                        </MenuItem>
                        <MenuItem
                          icon={
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z" fill="currentColor"/>
                            </svg>
                          }
                          _hover={{}}
                          onClick={() => {
                            // Save current page before navigating
                            localStorage.setItem('cases_last_page', String(currentPage));
                            navigate(`/reports/photo/${caseItem.id}`);
                          }}
                        >
                          تقرير الصور
                        </MenuItem>
                        {canEditCase() && (
                          <MenuItem
                            icon={
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" fill="currentColor"/>
                              </svg>
                            }
                            _hover={{}}
                            onClick={() => handleOpenModal(caseItem)}
                          >
                            تعديل
                          </MenuItem>
                        )}
                        {canDeleteCase() && (
                          <MenuItem
                            icon={
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" fill="currentColor"/>
                              </svg>
                            }
                            onClick={() => handleDeleteClick(caseItem.id)}
                            color="red.500"
                            _hover={{}}
                          >
                            حذف
                          </MenuItem>
                        )}
                      </MenuList>
                    </Menu>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      ) : (
        // Card view for small screens (mobile)
        <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }} gap={6}>
          {displayCases.map((caseItem) => (
            <Card key={caseItem.id} boxShadow="md" _hover={{ boxShadow: 'lg', transform: 'translateY(-2px)' }} transition="all 0.2s">
              <CardHeader>
                <VStack align="stretch" spacing={3} display={{ base: 'flex', md: 'none' }}>
                  <HStack spacing={2} justify="flex-end" flexWrap="wrap">
                    <IconButton
                      aria-label="عرض القضية"
                      icon={
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" fill="currentColor"/>
                        </svg>
                      }
                      size="sm"
                      variant="ghost"
                      colorScheme="blue"
                      onClick={() => {
                        setViewingCase(caseItem);
                        onViewCaseOpen();
                      }}
                      title="عرض القضية"
                    />
                    <IconButton
                      aria-label="سجل العمليات"
                      icon={
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z" fill="currentColor"/>
                        </svg>
                      }
                      size="sm"
                      variant="ghost"
                      colorScheme="blue"
                      onClick={() => {
                        setSelectedCaseId(caseItem.id);
                        onAuditLogOpen();
                      }}
                      title="سجل العمليات"
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        // Save current page before navigating
                        localStorage.setItem('cases_last_page', String(currentPage));
                        navigate(`/samples/${caseItem.id}`);
                      }}
                    >
                      العينات
                    </Button>
                    {canEditCase() && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleOpenModal(caseItem)}
                      >
                        تعديل
                      </Button>
                    )}
                    {canDeleteCase() && (
                      <Button
                        size="sm"
                        colorScheme="red"
                        onClick={() => handleDeleteClick(caseItem.id)}
                      >
                        حذف
                      </Button>
                    )}
                  </HStack>
                  <Heading size="md" color={caseNumberColor} dir="ltr" textAlign="right" w="100%">
                    {caseItem.caseNumber}
                  </Heading>
                </VStack>
                <Flex justify="space-between" align="flex-start" display={{ base: 'none', md: 'flex' }}>
                  <Heading size="md" color={caseNumberColor} dir="ltr" textAlign="left">
                    {caseItem.caseNumber}
                  </Heading>
                  <HStack spacing={2}>
                    <IconButton
                      aria-label="عرض القضية"
                      icon={
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" fill="currentColor"/>
                        </svg>
                      }
                      size="sm"
                      variant="ghost"
                      colorScheme="blue"
                      onClick={() => {
                        setViewingCase(caseItem);
                        onViewCaseOpen();
                      }}
                      title="عرض القضية"
                    />
                    <IconButton
                      aria-label="سجل العمليات"
                      icon={
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z" fill="currentColor"/>
                        </svg>
                      }
                      size="sm"
                      variant="ghost"
                      colorScheme="blue"
                      onClick={() => {
                        setSelectedCaseId(caseItem.id);
                        onAuditLogOpen();
                      }}
                      title="سجل العمليات"
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        // Save current page before navigating
                        localStorage.setItem('cases_last_page', String(currentPage));
                        navigate(`/samples/${caseItem.id}`);
                      }}
                    >
                      العينات
                    </Button>
                    {canEditCase() && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleOpenModal(caseItem)}
                      >
                        تعديل
                      </Button>
                    )}
                    {canDeleteCase() && (
                      <Button
                        size="sm"
                        colorScheme="red"
                        onClick={() => handleDeleteClick(caseItem.id)}
                      >
                        حذف
                      </Button>
                    )}
                  </HStack>
                </Flex>
              </CardHeader>
              <CardBody>
                <VStack align="stretch" spacing={3}>
                  <Flex justify="space-between">
                    <Text fontWeight="semibold" fontSize="sm" color="gray.600">
                      رقم خطاب الإحالة:
                    </Text>
                    <Text fontSize="sm">{caseItem.referenceLetterNumber || 'غير محدد'}</Text>
                  </Flex>
                  <Flex justify="space-between">
                    <Text fontWeight="semibold" fontSize="sm" color="gray.600">
                      الجهة الطالبة:
                    </Text>
                    <Text fontSize="sm">{caseItem.requestingAuthority || 'غير محدد'}</Text>
                  </Flex>
                  <Flex justify="space-between">
                    <Text fontWeight="semibold" fontSize="sm" color="gray.600">
                      تاريخ الخطاب:
                    </Text>
                    <Text fontSize="sm">{caseItem.letterDateHijriFormatted || 'غير محدد'}</Text>
                  </Flex>
                  <Box>
                    <Text fontWeight="semibold" fontSize="sm" color="gray.600" mb={2}>
                      موضوع القضية:
                    </Text>
                    <Text fontSize="sm">{caseItem.caseSubject || 'غير محدد'}</Text>
                  </Box>
                  {caseItem.category && (
                    <Flex justify="space-between">
                      <Text fontWeight="semibold" fontSize="sm" color="gray.600">
                        الصنف:
                      </Text>
                      <Text fontSize="sm">{caseItem.category.name || 'غير محدد'}</Text>
                    </Flex>
                  )}
                  {caseItem.type && (
                    <Flex justify="space-between">
                      <Text fontWeight="semibold" fontSize="sm" color="gray.600">
                        النوع:
                      </Text>
                      <Text fontSize="sm">{caseItem.type.name || 'غير محدد'}</Text>
                    </Flex>
                  )}
                </VStack>
              </CardBody>
              <CardFooter borderTop="1px solid" borderColor="gray.200" pt={4}>
                <HStack spacing={2} w="100%">
                  <Button
                    size="sm"
                    colorScheme="primary"
                    flex={1}
                    onClick={() => {
                      // Save current page before navigating
                      localStorage.setItem('cases_last_page', String(currentPage));
                      navigate(`/reports/technical/${caseItem.id}`);
                    }}
                  >
                    التقرير الفني
                  </Button>
                  <Button
                    size="sm"
                    colorScheme="gray"
                    flex={1}
                    onClick={() => {
                      // Save current page before navigating
                      localStorage.setItem('cases_last_page', String(currentPage));
                      navigate(`/reports/photo/${caseItem.id}`);
                    }}
                  >
                    تقرير الصور
                  </Button>
                </HStack>
              </CardFooter>
            </Card>
          ))}
        </Grid>
      )}

      {/* Pagination */}
      {casesPagination && casesPagination.totalPages > 1 && (
        <Box mt={6}>
          <Flex 
            direction={{ base: 'column', md: 'row' }}
            justifyContent="center" 
            alignItems="center" 
            gap={{ base: 3, md: 4 }}
            flexWrap="wrap"
          >
            {/* Navigation Buttons */}
            <HStack spacing={2}>
              <IconButton
                aria-label="الصفحة السابقة"
                onClick={() => {
                  if (currentPage > 1) {
                    setCurrentPage(currentPage - 1);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }
                }}
                isDisabled={currentPage === 1 || loading}
                variant="outline"
                size={buttonSize}
                fontSize="xl"
                colorScheme="gray"
              >
                <Text as="span" color="currentColor" fontWeight="bold">←</Text>
              </IconButton>
              
              {/* Page Numbers - Hidden on small screens, shown on medium+ */}
              {showPageNumbers && (
                <HStack spacing={1}>
                  {(() => {
                    const totalPages = casesPagination.totalPages || 1;
                    const current = currentPage;
                    const pages = [];
                    
                    // Show first page
                    if (current > 3) {
                      pages.push(1);
                      if (current > 4) {
                        pages.push('...');
                      }
                    }
                    
                    // Show pages around current
                    const start = Math.max(1, current - 2);
                    const end = Math.min(totalPages, current + 2);
                    
                    for (let i = start; i <= end; i++) {
                      pages.push(i);
                    }
                    
                    // Show last page
                    if (current < totalPages - 2) {
                      if (current < totalPages - 3) {
                        pages.push('...');
                      }
                      pages.push(totalPages);
                    }
                    
                    return pages.map((page, index) => {
                      if (page === '...') {
                        return (
                          <Text key={`ellipsis-${index}`} px={1} color="gray.500" fontSize="sm">
                            ...
                          </Text>
                        );
                      }
                      
                      return (
                        <Button
                          key={page}
                          size={pageButtonSize}
                          minW={{ base: '32px', md: '40px' }}
                          onClick={() => {
                            setCurrentPage(page);
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                          }}
                          isDisabled={loading}
                          colorScheme={page === current ? 'primary' : 'gray'}
                          variant={page === current ? 'solid' : 'outline'}
                        >
                          {page}
                        </Button>
                      );
                    });
                  })()}
                </HStack>
              )}
              
              {/* Current page indicator for mobile */}
              <Text 
                display={{ base: 'block', sm: 'none' }} 
                fontSize="sm" 
                fontWeight="semibold"
                px={2}
              >
                {currentPage} / {casesPagination.totalPages || 1}
              </Text>
              
              <IconButton
                aria-label="الصفحة التالية"
                onClick={() => {
                  const totalPages = casesPagination.totalPages || 1;
                  if (currentPage < totalPages) {
                    setCurrentPage(currentPage + 1);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }
                }}
                isDisabled={currentPage >= (casesPagination.totalPages || 1) || loading}
                variant="outline"
                size={buttonSize}
                fontSize="xl"
                colorScheme="gray"
              >
                <Text as="span" color="currentColor" fontWeight="bold">→</Text>
              </IconButton>
            </HStack>
            
            {/* Page Info - Hidden on small screens */}
            {showPageInfo && (
              <Text 
                fontSize="sm" 
                color="gray.600"
                textAlign="center"
              >
                صفحة {currentPage} من {casesPagination.totalPages || 1} ({casesPagination.totalItems || casesPagination.total || 0} قضية)
              </Text>
            )}
          </Flex>
        </Box>
      )}

      <Modal isOpen={isOpen} onClose={handleCloseModal} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {editingCase ? 'تعديل قضية' : 'إضافة قضية جديدة'}
          </ModalHeader>
          <ModalCloseButton />
          <form onSubmit={handleSubmit}>
            <ModalBody>
              <VStack spacing={4}>
                <FormControl isRequired>
                  <FormLabel>رقم خطاب الإحالة</FormLabel>
                  <Input
                    value={formData.referenceLetterNumber}
                    onChange={(e) => {
                      const convertedValue = arabicToEnglish(e.target.value);
                      setFormData({ ...formData, referenceLetterNumber: convertedValue });
                    }}
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>الجهة الطالبة أو مرجع الوثيقة</FormLabel>
                  <Input
                    value={formData.requestingAuthority}
                    onChange={(e) => {
                      const convertedValue = arabicToEnglish(e.target.value);
                      setFormData({ ...formData, requestingAuthority: convertedValue });
                    }}
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>تاريخ الخطاب</FormLabel>
                  <HijriDatePicker
                    value={formData.letterDate}
                    onChange={(date) =>
                      setFormData({ ...formData, letterDate: date })
                    }
                    placeholder="اختر تاريخ الخطاب"
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>موضوع القضية</FormLabel>
                  <Textarea
                    value={formData.caseSubject}
                    onChange={(e) =>
                      setFormData({ ...formData, caseSubject: e.target.value })
                    }
                    rows={4}
                  />
                </FormControl>

                <SearchableSelect
                  label="صنف القضية"
                  items={categories}
                  value={formData.categoryId}
                  isLoading={loadingCategories}
                  // قفل الصنف أثناء تحميل الأنواع حتى لا يغير المستخدم والريسبونس ما جاء
                  isDisabled={loadingCategories || loadingTypes}
                  placeholder="اختر/ابحث عن الصنف"
                  getLabel={(c) => c.name}
                  getValue={(c) => c.id}
                  onChange={(newCategoryId) => {
                    setFormData({
                      ...formData,
                      categoryId: newCategoryId,
                      typeId: '',
                    });
                    setTypes([]);
                  }}
                />

                <SearchableSelect
                  label="نوع القضية"
                  items={types}
                  value={formData.typeId}
                  isLoading={loadingTypes}
                  isDisabled={!formData.categoryId || loadingTypes}
                  placeholder={formData.categoryId ? 'اختر/ابحث عن النوع' : 'اختر الصنف أولاً'}
                  getLabel={(t) => t.name}
                  getValue={(t) => t.id}
                  onChange={(newTypeId) => {
                    setFormData({ ...formData, typeId: newTypeId });
                  }}
                />

                <FormControl>
                  <FormLabel>المستخدمون المعينون</FormLabel>
                  <Box
                    maxH="200px"
                    overflowY="auto"
                    p={4}
                    bg={assignedUsersBg}
                    borderRadius="md"
                    border="1px solid"
                    borderColor={borderColor}
                  >
                    <VStack align="stretch" spacing={3}>
                      {users
                        .filter(user => {
                          // Hide System Administrator from assignment list
                          if (user.role === 'System Administrator') {
                            return false;
                          }
                          // If current user has a location, only show users from the same location
                          if (currentUser && (currentUser.locationId || currentUser.location_id)) {
                            const userLocationId = currentUser.locationId || currentUser.location_id;
                            const targetUserLocationId = user.locationId || user.location_id;
                            return String(userLocationId) === String(targetUserLocationId);
                          }
                          // If current user has no location (e.g., System Administrator), show all users
                          return true;
                        })
                        .map((user) => {
                        const isAssigned = formData.assignedUsers?.some(id => String(id) === String(user.id)) || false;
                        return (
                          <Box
                            key={user.id}
                            p={3}
                            bg={assignedUserCardBg}
                            borderRadius="md"
                            border="1px solid"
                            borderColor={isAssigned ? 'primary.500' : borderColor}
                            cursor="pointer"
                            _hover={{
                              borderColor: 'primary.300',
                              bg: assignedUserCardHoverBg,
                            }}
                            transition="all 0.2s"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              toggleUserAssignment(user.id);
                            }}
                          >
                            <Checkbox
                              isChecked={isAssigned}
                              onChange={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                toggleUserAssignment(user.id);
                              }}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                toggleUserAssignment(user.id);
                              }}
                              colorScheme="primary"
                              size="lg"
                              w="100%"
                            >
                              <VStack align="flex-start" spacing={0} ml={2} flex={1}>
                                <Text fontWeight="semibold" fontSize="sm">
                                  {user.fullName}
                                </Text>
                                <Text fontSize="xs" color={assignedUserTextColor}>
                                  {user.username}
                                </Text>
                              </VStack>
                            </Checkbox>
                          </Box>
                        );
                      })}
                    </VStack>
                  </Box>
                </FormControl>
              </VStack>
            </ModalBody>
            <Box p={6} borderTop="1px solid" borderColor="gray.200">
              <HStack justify="flex-end" spacing={3}>
                <Button variant="outline" onClick={handleCloseModal}>
                  إلغاء
                </Button>
                <Button type="submit" colorScheme="primary" isLoading={isSubmitting} loadingText={editingCase ? 'جاري الحفظ...' : 'جاري الإضافة...'}>
                  {editingCase ? 'حفظ التعديلات' : 'إضافة'}
                </Button>
              </HStack>
            </Box>
          </form>
        </ModalContent>
      </Modal>

      <AlertDialog isOpen={isDeleteOpen} onClose={onDeleteClose} leastDestructiveRef={undefined}>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              حذف القضية
            </AlertDialogHeader>
            <AlertDialogBody>
              هل أنت متأكد من حذف هذه القضية؟ سيتم حذف جميع العينات والآثار المرتبطة بها.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button onClick={onDeleteClose}>
                إلغاء
              </Button>
              <Button colorScheme="red" onClick={handleDeleteConfirm} mr={3} isLoading={isDeleting} loadingText="جاري الحذف...">
                حذف
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      <AuditLogModal
        isOpen={isAuditLogOpen}
        onClose={onAuditLogClose}
        caseId={selectedCaseId}
        caseNumber={cases.find(c => c.id === selectedCaseId)?.caseNumber}
      />

      {/* View Case Modal */}
      <Modal isOpen={isViewCaseOpen} onClose={onViewCaseClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>عرض بيانات القضية</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {viewingCase && (
              <VStack align="stretch" spacing={4}>
                <Box>
                  <Text fontWeight="semibold" color={modalLabelColor} mb={1}>
                    رقم القضية
                  </Text>
                  <Text fontSize="lg" color="primary.600" dir="ltr" textAlign="left">
                    {viewingCase.caseNumber}
                  </Text>
                </Box>

                <Box>
                  <Text fontWeight="semibold" color={modalLabelColor} mb={1}>
                    رقم خطاب الإحالة
                  </Text>
                  <Text color={modalValueColor}>{viewingCase.referenceLetterNumber || 'غير محدد'}</Text>
                </Box>

                <Box>
                  <Text fontWeight="semibold" color={modalLabelColor} mb={1}>
                    الجهة الطالبة
                  </Text>
                  <Text color={modalValueColor}>{viewingCase.requestingAuthority || 'غير محدد'}</Text>
                </Box>

                <Box>
                  <Text fontWeight="semibold" color={modalLabelColor} mb={1}>
                    تاريخ الخطاب (هجري)
                  </Text>
                  <Text color={modalValueColor}>{viewingCase.letterDateHijriFormatted || 'غير محدد'}</Text>
                </Box>

                {viewingCase.letterDate && (
                  <Box>
                    <Text fontWeight="semibold" color={modalLabelColor} mb={1}>
                      تاريخ الخطاب (ميلادي)
                    </Text>
                    <Text color={modalValueColor}>{new Date(viewingCase.letterDate).toLocaleDateString('ar-SA')}</Text>
                  </Box>
                )}

                <Box>
                  <Text fontWeight="semibold" color={modalLabelColor} mb={1}>
                    موضوع القضية
                  </Text>
                  <Text whiteSpace="pre-wrap" color={modalValueColor}>{viewingCase.caseSubject || 'غير محدد'}</Text>
                </Box>

                {viewingCase.category && (
                  <Box>
                    <Text fontWeight="semibold" color={modalLabelColor} mb={1}>
                      الصنف
                    </Text>
                    <Text color={modalValueColor}>{viewingCase.category.name || 'غير محدد'}</Text>
                  </Box>
                )}

                {viewingCase.type && (
                  <Box>
                    <Text fontWeight="semibold" color={modalLabelColor} mb={1}>
                      النوع
                    </Text>
                    <Text color={modalValueColor}>{viewingCase.type.name || 'غير محدد'}</Text>
                  </Box>
                )}

                {viewingCase.location && (
                  <Box>
                    <Text fontWeight="semibold" color={modalLabelColor} mb={1}>
                      الموقع
                    </Text>
                    <Text color={modalValueColor}>
                      {viewingCase.location.name || 'غير محدد'}
                      {viewingCase.location.region && ` - ${viewingCase.location.region}`}
                      {viewingCase.location.city && ` - ${viewingCase.location.city}`}
                    </Text>
                  </Box>
                )}

                {viewingCase.assignedUsers && viewingCase.assignedUsers.length > 0 && (
                  <Box>
                    <Text fontWeight="semibold" color={modalLabelColor} mb={2}>
                      المستخدمون المعينون
                    </Text>
                    <VStack align="stretch" spacing={2}>
                      {viewingCase.assignedUsers.map((user) => (
                        <Box
                          key={user.id}
                          p={3}
                          bg={modalUserCardBg}
                          borderRadius="md"
                          border="1px solid"
                          borderColor={modalUserCardBorder}
                        >
                          <Text fontWeight="medium" color={modalValueColor}>{user.fullName || user.full_name}</Text>
                          <Text fontSize="sm" color={modalUserTextColor}>
                            {user.username} - {user.role}
                          </Text>
                        </Box>
                      ))}
                    </VStack>
                  </Box>
                )}

                {viewingCase.created_at && (
                  <Box>
                    <Text fontWeight="semibold" color={modalLabelColor} mb={1}>
                      تاريخ الإنشاء
                    </Text>
                    <Text color={modalValueColor}>{new Date(viewingCase.created_at).toLocaleString('ar-SA')}</Text>
                  </Box>
                )}

                {viewingCase.updated_at && (
                  <Box>
                    <Text fontWeight="semibold" color={modalLabelColor} mb={1}>
                      آخر تحديث
                    </Text>
                    <Text color={modalValueColor}>{new Date(viewingCase.updated_at).toLocaleString('ar-SA')}</Text>
                  </Box>
                )}
              </VStack>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default CaseManagement;
